'use client';

import Link from 'next/link';
import * as NavigationMenu from '@radix-ui/react-navigation-menu';
import { usePathname } from 'next/navigation';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { APP_URL } from '@/lib/config';
import { HAS_OPEN_ROLES } from '@/config/jobs';
import { atlasUseCases } from '@/config/products';

const atlasIcons: Record<string, string> = {
  '/prospecting': 'solar--radar-2-linear',
  '/underwriting': 'solar--shield-check-linear',
  '/investor-tracking': 'solar--chart-2-linear',
};

// The Atlas drop-down panel keeps the site's original menu style, under its trigger.
// Drop-down rows: hovered, focused or current rows get a soft blue tint, with the title in deep blue and the icon in action blue
const menuRowClass = 'group/row flex gap-3 rounded-md px-3 py-3 transition-colors hover:bg-blue-50 hover:text-blue-700 focus-visible:bg-blue-50 focus-visible:text-blue-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 data-[active]:bg-blue-50 data-[active]:text-blue-700';
const panelClass = 'absolute left-0 top-full mt-3 max-h-[calc(100svh-var(--site-nav-height,65px)-24px)] w-[360px] overflow-y-auto overscroll-contain rounded-lg border border-zinc-200 bg-white text-zinc-900 shadow-lg';

// The four main links, each with a short subline under its name. Atlas and Recon's say what each product is.
const mainLinks = [
  { key: 'atlas', label: 'Atlas', sub: 'Open data portal' },
  { key: '/recon', label: 'Recon', sub: 'Underwriting agent' },
  { key: '/technology', label: 'Technology', sub: 'New ownership' },
  { key: '/faq', label: 'FAQ', sub: 'How we are different' },
] as const;

type NavigationProps = {
  variant?: 'home' | 'subpage';
};

export default function Navigation({ variant = 'subpage' }: NavigationProps) {
  const pathname = usePathname();
  const [isOverDark, setIsOverDark] = useState(variant === 'home');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navRef = useRef<HTMLElement>(null);
  const mobileToggleRef = useRef<HTMLButtonElement>(null);
  const [desktopMenu, setDesktopMenu] = useState('');

  const isActive = (href: string) => pathname === href;
  const isAtlasActive = pathname === '/' || atlasUseCases.some(item => isActive(item.href));

  // Keep page content clear of the fixed navigation, including any banner wrapping.
  useLayoutEffect(() => {
    const nav = navRef.current;
    if (!nav) return;

    const updateNavHeight = () => {
      document.documentElement.style.setProperty('--site-nav-height', `${nav.getBoundingClientRect().height}px`);
    };

    updateNavHeight();
    const observer = new ResizeObserver(updateNavHeight);
    observer.observe(nav);

    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty('--site-nav-height');
    };
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
    setDesktopMenu('');
  }, [pathname]);

  useEffect(() => {
    if (!mobileMenuOpen) return;
    const dismiss = (event: PointerEvent) => {
      if (event.target instanceof Node && !navRef.current?.contains(event.target)) setMobileMenuOpen(false);
    };
    document.addEventListener('pointerdown', dismiss);
    return () => document.removeEventListener('pointerdown', dismiss);
  }, [mobileMenuOpen]);

  // Runs before the first paint, so a page opening under a dark banner never shows the light nav for a frame
  useLayoutEffect(() => {
    const checkNavOverDark = () => {
      const navHeight = navRef.current?.getBoundingClientRect().height ?? 0;
      const navBottom = navHeight;

      // Find all dark sections
      const darkSections = document.querySelectorAll('[data-nav-theme="dark"]');

      let overDark = false;
      darkSections.forEach((section) => {
        const rect = section.getBoundingClientRect();
        // Check if the nav overlaps with this section
        if (rect.top < navBottom && rect.bottom > 0) {
          overDark = true;
        }
      });

      setIsOverDark(overDark);
    };

    checkNavOverDark();
    window.addEventListener('scroll', checkNavOverDark);
    window.addEventListener('resize', checkNavOverDark);

    return () => {
      window.removeEventListener('scroll', checkNavOverDark);
      window.removeEventListener('resize', checkNavOverDark);
    };
  }, []);

  const showLightNav = isOverDark;
  // Same look as the Technology and FAQ links, with a chevron
  // A mouse click on a trigger whose menu is already open (usually from hovering) keeps it open
  // instead of toggling it shut. Keyboard Enter/Space (detail 0) still toggles as usual.
  const keepOpenOnClick = (value: string) => (event: React.MouseEvent) => {
    if (event.detail > 0 && desktopMenu === value) event.preventDefault();
  };
  const triggerClass = (active: boolean) => `group flex items-center gap-1.5 rounded text-sm font-medium outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-4 ${showLightNav ? `${active ? 'text-white' : 'text-zinc-300'} hover:text-white data-[state=open]:text-white focus-visible:ring-offset-zinc-900` : `${active ? 'text-zinc-900' : 'text-zinc-600'} hover:text-zinc-900 data-[state=open]:text-zinc-900 focus-visible:ring-offset-white`}`;

  // Wide screens: a taller bar where every link (name over subline) and the larger buttons are 44px tall on one centre line
  const subColor = showLightNav ? 'text-[#81818b] group-hover:text-zinc-400' : 'text-zinc-500 group-hover:text-zinc-600';
  const newBadge = <span className={`rounded px-2 py-0.5 text-[11px] font-medium leading-3 ${showLightNav ? 'bg-emerald-400/10 text-emerald-300' : 'bg-emerald-50 text-emerald-700'}`}>New</span>;
  const linkLabel = (key: string, label: string, sub: string, chevron: boolean) => (
    <span className="flex h-11 flex-col items-start justify-center">
      <span className="flex items-center gap-1.5 leading-5 tracking-[0.6px]">
        {label}
        {chevron && <span aria-hidden="true" className="iconify solar--alt-arrow-down-linear text-sm transition-transform group-data-[state=open]:rotate-180" />}
        {key === '/recon' && newBadge}
      </span>
      <span className={`text-xs font-normal leading-4 transition-colors ${subColor}`}>{sub}</span>
    </span>
  );
  const linkClass = `group rounded text-sm font-medium outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-4 ${showLightNav ? 'text-zinc-300 hover:text-white focus-visible:ring-offset-zinc-900' : 'text-zinc-600 hover:text-zinc-900 focus-visible:ring-offset-white'}`;
  const renderLinks = (keys: readonly string[]) => (
    <div className="hidden lg:flex whitespace-nowrap items-center gap-7 xl:gap-9">
      {keys.map(key => {
        const link = mainLinks.find(l => l.key === key)!;
        const sub = link.sub;
        if (key === 'atlas') return (
          <NavigationMenu.Root key={key} asChild value={desktopMenu} onValueChange={setDesktopMenu} delayDuration={120}>
            <div className="relative">
              <NavigationMenu.List className="flex items-center">
                <NavigationMenu.Item value="atlas" className="relative">
                  <NavigationMenu.Trigger className={triggerClass(isAtlasActive)} onClick={keepOpenOnClick('atlas')}>
                    {linkLabel(key, link.label, sub, true)}
                  </NavigationMenu.Trigger>
                  {/* The site's original Atlas menu: its three use cases */}
                  <NavigationMenu.Content className={panelClass}>
                    <div className="px-5 pt-5 pb-3"><p className="text-xs font-medium uppercase tracking-widest text-zinc-600">Explore Atlas</p><p className="mt-2 text-sm leading-relaxed text-zinc-600">Real estate data for private lending.</p></div>
                    <ul className="px-2 pb-2">
                      {atlasUseCases.map(item => <li key={item.href}><NavigationMenu.Link asChild active={isActive(item.href)}><Link href={item.href} aria-current={isActive(item.href) ? 'page' : undefined} className={menuRowClass}><span aria-hidden="true" className={`iconify ${atlasIcons[item.href]} mt-0.5 shrink-0 text-xl text-zinc-600 group-hover/row:text-blue-600 group-focus-visible/row:text-blue-600 group-data-[active]/row:text-blue-600`} /><span><span className="block text-sm font-medium">{item.label}</span><span className="mt-1 block whitespace-normal text-xs leading-relaxed text-zinc-600">{item.description}</span></span></Link></NavigationMenu.Link></li>)}
                    </ul>
                  </NavigationMenu.Content>
                </NavigationMenu.Item>
              </NavigationMenu.List>
            </div>
          </NavigationMenu.Root>
        );
        return <Link key={key} href={key} aria-current={isActive(key) ? 'page' : undefined} className={linkClass}>{linkLabel(key, link.label, sub, false)}</Link>;
      })}
    </div>
  );

  return (
    <nav
      ref={navRef}
      id="navbar"
      aria-label="Main navigation"
      onKeyDown={event => {
        if (event.key === 'Escape' && mobileMenuOpen) {
          setMobileMenuOpen(false);
          mobileToggleRef.current?.focus();
        }
      }}
      className={`fixed top-0 left-0 right-0 z-50 border-b backdrop-blur-md transition-all duration-300 ${showLightNav
        ? 'bg-transparent border-white/10'
        : 'bg-white/80 border-zinc-200'
        }`}
    >
      {HAS_OPEN_ROLES ? (
        <Link
          href="/careers"
          className={`group block border-b transition-colors ${showLightNav
            ? 'bg-zinc-950/40 border-white/10 hover:bg-zinc-950/60'
            : 'bg-zinc-900 border-zinc-800 hover:bg-zinc-800'
            }`}
        >
          <div className="max-w-7xl mx-auto px-6 min-h-9 py-2 flex items-center justify-center gap-2 text-xs sm:text-sm">
            <span aria-hidden="true" className="relative flex h-2 w-2 shrink-0">
              <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 motion-safe:animate-ping"></span>
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400"></span>
            </span>
            <span className="font-medium text-white">We&apos;re hiring</span>
            <span className="inline-flex items-center gap-1 text-zinc-200 group-hover:text-white transition-colors">
              <span className="hidden sm:inline">View open roles</span>
              <span className="sm:hidden">See roles</span>
              <span className="iconify solar--arrow-right-linear text-sm transition-transform group-hover:translate-x-0.5"></span>
            </span>
          </div>
        </Link>
      ) : null}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 gap-2 flex items-center justify-between min-h-16 lg:min-h-20">
        <div className="flex items-center gap-4 lg:gap-[60px]">
          <Link href="/" className="flex shrink-0 items-center gap-2 group">
            <img
              src={showLightNav ? '/assets/logos/logo_wide_white_color.svg' : '/assets/logos/logo_wide_gray_color.svg'}
              alt="Elementix"
              width={130}
              height={20}
              className="h-5 w-auto"
            />
          </Link>
          {renderLinks(mainLinks.map(l => l.key))}
        </div>

        {/* Mobile Menu Icon + CTA (right-aligned) */}
        <div className="flex shrink-0 items-center gap-2 sm:gap-3">
          <button
            ref={mobileToggleRef}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`lg:hidden transition-colors ${showLightNav ? 'text-white' : 'text-zinc-700'
              }`}
            aria-label="Toggle menu"
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-navigation"
          >
            {mobileMenuOpen ? (
              <span className="iconify solar--close-circle-linear text-2xl"></span>
            ) : (
              <span className="iconify solar--hamburger-menu-linear text-2xl"></span>
            )}
          </button>
          <a
            href={APP_URL}
            className={`ex-fill hidden lg:flex items-center gap-2 px-5 h-11 text-[15px] font-medium rounded-[var(--ex-button-radius)] shadow-sm ${showLightNav
              ? '[--ex-button-bg:rgb(39_39_42/0.5)] text-zinc-300'
              : '[--ex-button-bg:#f4f4f5] text-zinc-600'
              }`}
          >
            Client Login
          </a>
          <a
            href="/contact"
            className={`ex-fill flex items-center gap-2 px-3 sm:px-4 lg:px-5 py-2 lg:py-0 lg:h-11 text-xs sm:text-[length:var(--ex-button-size)] lg:text-[15px] whitespace-nowrap font-medium rounded-[var(--ex-button-radius)] shadow-sm ${showLightNav
              ? '[--ex-button-bg:#fff] text-zinc-700'
              : '[--ex-button-bg:#18181b] text-white'
              }`}
          >
            Book a Demo
            <span className="iconify solar--arrow-right-linear text-sm hidden sm:inline-block"></span>
          </a>
        </div>
      </div>

      {/* Mobile Menu Panel */}
      {mobileMenuOpen && (
        <div id="mobile-navigation" className={`absolute top-full inset-x-0 max-h-[calc(100svh-var(--site-nav-height,65px))] overflow-y-auto lg:hidden border-t ${showLightNav ? 'bg-zinc-900 border-white/10 text-zinc-100' : 'bg-white border-zinc-200 text-zinc-900'}`}>
          <div className="px-6 py-5">
            <Link href="/" onClick={() => setMobileMenuOpen(false)} className="flex items-center justify-between rounded py-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500">
              <span><span className="block text-base font-semibold">Atlas</span><span className={`mt-0.5 block text-xs ${showLightNav ? 'text-zinc-400' : 'text-zinc-500'}`}>Open data portal</span></span>
            </Link>
            <div className={`my-3 ml-1 border-l pl-4 ${showLightNav ? 'border-zinc-700' : 'border-zinc-200'}`}>
              {atlasUseCases.map(item => <Link key={item.href} href={item.href} aria-current={isActive(item.href) ? 'page' : undefined} onClick={() => setMobileMenuOpen(false)} className="flex gap-3 rounded py-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500"><span aria-hidden="true" className={`iconify ${atlasIcons[item.href]} mt-0.5 shrink-0 text-lg ${showLightNav ? 'text-zinc-400' : 'text-zinc-600'}`} /><span><span className="block text-sm font-medium">{item.label}</span><span className={`mt-1 block text-xs leading-relaxed ${showLightNav ? 'text-zinc-400' : 'text-zinc-600'}`}>{item.description}</span></span></Link>)}
            </div>
            <Link href="/recon" aria-current={isActive('/recon') ? 'page' : undefined} onClick={() => setMobileMenuOpen(false)} className={`flex items-center justify-between border-y py-5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500 ${showLightNav ? 'border-zinc-700' : 'border-zinc-200'}`}>
              <span><span className="flex items-center gap-2 text-base font-semibold">Recon<span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${showLightNav ? 'bg-emerald-400/10 text-emerald-300' : 'bg-emerald-50 text-emerald-700'}`}>New</span></span><span className={`mt-0.5 block text-xs ${showLightNav ? 'text-zinc-400' : 'text-zinc-500'}`}>Underwriting agent</span></span><span aria-hidden="true" className="iconify solar--arrow-right-linear" />
            </Link>
            <div className={`flex flex-wrap gap-x-7 gap-y-3 pt-5 text-sm ${showLightNav ? 'text-zinc-300' : 'text-zinc-600'}`}>
              <Link href="/technology" onClick={() => setMobileMenuOpen(false)}>Technology</Link>
              <Link href="/faq" onClick={() => setMobileMenuOpen(false)}>FAQ</Link>
              <a href={APP_URL}>Client Login</a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
